// 付箋・テキスト図形・セクション名の本文長上限と、その上限へ本文を収める純粋関数。
//
// 背景: 本文が上限を超えると、従来はサーバが `object.text.update` を拒否し、楽観反映が
// 巻き戻って貼り付けた内容が丸ごと消えていた。上限までは確実に取り込み、入りきらない分だけを
// 切り詰める、という責務をこのモジュールへ集約する。
// クライアントの各経路（編集器・dispatcher の最終防衛）は同じ契約でこの関数を参照し、
// 経路ごとに切り詰め方が違う実装を作らない。サーバは切り詰めを行わないが、上限値だけを共有する。

/**
 * 付箋・テキスト図形・セクション名の本文として保存できる最大長（UTF-16 コードユニット数）。
 * API Gateway WebSocket の frame size 上限 32 KB は引き上げられないため、送信メッセージは
 * 30,720 バイト（websocket-realtime の CLIENT_MESSAGE_MAX_BYTES）に収める必要がある。
 * 日本語は UTF-8 で 1 文字 3 バイトのため、本値は本文だけで約 24.6 KB を占める。
 * この値は「保存できる文字数の希望値」ではなく、引き上げ不可のフレーム制約から導いた性能限界である。
 */
export const TEXT_BODY_MAX_LENGTH = 8192;

/** どの上限に達して切り詰めたか。`none` は切り詰めなし。 */
export type TextBodyClampReason = "none" | "length" | "bytes";

export interface TextBodyClampResult {
  /** 上限に収めた本文。 */
  readonly text: string;
  /** 収めた結果のキャレット位置（UTF-16 コードユニットのオフセット）。 */
  readonly caret: number;
  /** 切り詰めた文字数（UTF-16 コードユニット数）。切り詰めていなければ 0。 */
  readonly droppedLength: number;
  /** 到達した上限。両方に掛かる場合は `length` を優先して返す。 */
  readonly reason: TextBodyClampReason;
}

function isHighSurrogate(code: number): boolean {
  return code >= 0xd800 && code <= 0xdbff;
}

function isLowSurrogate(code: number): boolean {
  return code >= 0xdc00 && code <= 0xdfff;
}

/**
 * キャレット位置を本文の範囲内へ丸め、サロゲートペアの中間を指していればコードポイント境界へ寄せる。
 *
 * ペアの中間で本文を分割すると、前半の末尾に上位サロゲート、後半の先頭に下位サロゲートが
 * 孤立して残る。切り詰めの前にここでそろえておくと、以降の処理はコードポイント境界だけを
 * 扱えばよくなる。
 */
export function normalizeCaretToCodePointBoundary(text: string, caret: number): number {
  if (!Number.isFinite(caret)) return text.length;
  const bounded = Math.max(0, Math.min(text.length, Math.floor(caret)));
  if (bounded <= 0 || bounded >= text.length) return bounded;
  return isHighSurrogate(text.charCodeAt(bounded - 1)) && isLowSurrogate(text.charCodeAt(bounded))
    ? bounded - 1
    : bounded;
}

/**
 * 1 コードユニットを JSON 文字列へ直列化したときの UTF-8 バイト数。
 * サロゲートペアは上位側で 4 バイトを計上し、下位側は 0 とする（ペアを分断しない前提のため、
 * 累積値はコードポイント境界でだけ参照される）。対になっていない孤立サロゲートは、
 * well-formed な `JSON.stringify` と同じく `\uXXXX`（6 バイト）へ展開される。
 */
function jsonUnitCost(text: string, index: number): number {
  const code = text.charCodeAt(index);
  // `"` と `\` はエスケープされて 2 バイトになる。
  if (code === 0x22 || code === 0x5c) return 2;
  if (code < 0x20) {
    // \b \t \n \f \r は 2 文字の短縮形。それ以外の C0 制御文字は \uXXXX の 6 バイト。
    return code === 0x08 || code === 0x09 || code === 0x0a || code === 0x0c || code === 0x0d
      ? 2
      : 6;
  }
  if (code < 0x80) return 1;
  if (code < 0x800) return 2;
  if (isHighSurrogate(code)) {
    return isLowSurrogate(text.charCodeAt(index + 1)) ? 4 : 6;
  }
  if (isLowSurrogate(code)) {
    // 直前が上位サロゲートなら、その 4 バイトへ既に計上済み。
    return index > 0 && isHighSurrogate(text.charCodeAt(index - 1)) ? 0 : 6;
  }
  return 3;
}

/**
 * JSON 文字列としての UTF-8 バイト数（囲みの引用符は含まない）。
 *
 * 生の UTF-8 長では予算を過小評価する。`"` / `\` は 2 バイト、制御文字は 2 または 6 バイト
 * （`\uXXXX`）へ展開されるためで、送信サイズ上限の判定にはこの数え方を使う。
 */
export function jsonStringByteLength(text: string): number {
  let total = 0;
  for (let index = 0; index < text.length; index += 1) total += jsonUnitCost(text, index);
  return total;
}

/** `costs[i]` に「先頭から i コードユニットぶん」の JSON バイト数を持つ累積配列を作る。 */
function jsonPrefixCosts(text: string): readonly number[] {
  const costs = new Array<number>(text.length + 1);
  costs[0] = 0;
  let total = 0;
  for (let index = 0; index < text.length; index += 1) {
    total += jsonUnitCost(text, index);
    costs[index + 1] = total;
  }
  return costs;
}

/**
 * 累積コストが予算以内に収まる最大の長さ（0..limit）を返す。
 * 累積配列は単調非減少なので二分探索で求まる。
 */
function longestWithinBudget(costs: readonly number[], limit: number, budget: number): number {
  if (budget < 0) return 0;
  let low = 0;
  let high = Math.max(0, Math.min(limit, costs.length - 1));
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if ((costs[mid] ?? 0) <= budget) low = mid;
    else high = mid - 1;
  }
  return low;
}

/** 上位サロゲートで終わる長さなら、1 コードユニット手前へ下げてペアの分断を防ぐ。 */
function avoidSplittingSurrogatePair(text: string, length: number): number {
  return length > 0 && isHighSurrogate(text.charCodeAt(length - 1)) ? length - 1 : length;
}

/**
 * 変更後の本文とキャレットを、文字数上限（と任意のバイト予算）へ収める。
 *
 * 切り詰めはキャレットの直前（＝いま挿入した部分の末尾）から行い、キャレットより後ろにあった
 * 既存本文は保持する。保存済み本文自体が既に上限を超えているという異常系でだけ、
 * 続けて後半の末尾も削る。戻り値は必ず両方の上限を満たす。
 */
export function clampTextBodyAtCaret(input: {
  readonly text: string;
  readonly caret: number;
  readonly maxLength: number;
  /** JSON 文字列としてのバイト予算（引用符を除く）。省略時は文字数だけで判定する。 */
  readonly maxBytes?: number | undefined;
}): TextBodyClampResult {
  const { text } = input;
  const maxLength = Number.isFinite(input.maxLength) ? Math.max(0, Math.floor(input.maxLength)) : 0;
  const maxBytes =
    input.maxBytes === undefined || !Number.isFinite(input.maxBytes)
      ? undefined
      : Math.max(0, Math.floor(input.maxBytes));
  const caret = normalizeCaretToCodePointBoundary(text, input.caret);

  const overLength = text.length > maxLength;
  const totalBytes = maxBytes === undefined ? 0 : jsonStringByteLength(text);
  const overBytes = maxBytes !== undefined && totalBytes > maxBytes;
  if (!overLength && !overBytes) {
    return { caret, droppedLength: 0, reason: "none", text };
  }
  // 文字数上限にも掛かっているなら、利用者へ説明しやすい固定値のほうを理由として伝える。
  const reason: TextBodyClampReason = overLength ? "length" : "bytes";

  const head = text.slice(0, caret);
  const tail = text.slice(caret);
  const tailBytes = maxBytes === undefined ? 0 : jsonStringByteLength(tail);

  // 前半から保持できる最大長。文字数とバイト数の両方の制約を満たす最大値を求める。
  let kept = Math.max(0, Math.min(head.length, maxLength - tail.length));
  if (maxBytes !== undefined) {
    kept = longestWithinBudget(jsonPrefixCosts(head), kept, maxBytes - tailBytes);
  }
  kept = avoidSplittingSurrogatePair(head, kept);

  // 前半を 0 まで削っても収まらない異常系（保存済み本文自体が上限超過）でだけ、後半の末尾も削る。
  if (kept === 0 && (tail.length > maxLength || (maxBytes !== undefined && tailBytes > maxBytes))) {
    let keptTail = Math.min(tail.length, maxLength);
    if (maxBytes !== undefined) {
      keptTail = longestWithinBudget(jsonPrefixCosts(tail), keptTail, maxBytes);
    }
    keptTail = avoidSplittingSurrogatePair(tail, keptTail);
    return {
      caret: 0,
      droppedLength: head.length + (tail.length - keptTail),
      reason,
      text: tail.slice(0, keptTail),
    };
  }

  return {
    caret: kept,
    droppedLength: head.length - kept,
    reason,
    text: head.slice(0, kept) + tail,
  };
}
